import type { RouteLocationNormalizedLoadedGeneric, RouterLinkProps } from 'vue-router';
import type { BadgeVariants } from './badge';
import type { AppID } from '../cross-app-shared/types/db.ts';

export type NavMenuGroup = {
  title: string;
  children: ({ title: string; icon?: string; customIcon?: string } & (
    | {
        type: 'collapsible';
        isActive: boolean;
        pages: { title: string; icon: `fa-${string}`; to: RouterLinkProps['to']; isActive: boolean }[];
      }
    | {
        type: 'link';
        to: RouterLinkProps['to'];
        activeRouteNames?: string[];
        isActive: boolean;
        badge?: { label: string; variant: BadgeVariants['variant'] };
        actions?: {
          title: string;
          icon: `fa-${string}`;
          action: () => unknown;
        }[];
      }
  ))[];
};

// Locally apps are <app>.localhost:<port>; deployed they are <app>.envirotoolkit.com or <app>-test.envirotoolkit.com.
export function appOrigin(app: AppID): string {
  return appOriginBuild(app, window.location, DEPLOYMENT_ENVIRONMENT);
}

export function appOriginBuild(
  app: AppID,
  location: Pick<Location, 'protocol' | 'hostname' | 'port'>,
  environment: 'test' | 'prod',
): string {
  const { protocol, hostname, port } = location;
  if (hostname === 'localhost' || hostname.endsWith('.localhost')) {
    return `${protocol}//${app}.localhost${port ? `:${port}` : ''}`;
  }
  return environment === 'test' ? `https://${app}-test.envirotoolkit.com` : `https://${app}.envirotoolkit.com`;
}

export type NavMenuSection = {
  id: 'workflow' | 'help' | 'admin';
  title: string;
  groups: NavMenuGroup[];
};

export function navActiveStatesUpdate(
  currentRoute: RouteLocationNormalizedLoadedGeneric,
  result: NavMenuSection[],
): void {
  for (const section of result) {
    for (const group of section.groups) {
      for (const child of group.children) {
        if (
          child.type === 'link' &&
          typeof child.to === 'object' &&
          'name' in child.to &&
          [child.to.name, ...(child.activeRouteNames ?? [])].includes(
            String(currentRoute.matched.filter((r) => !!r.name)[0]?.name),
          )
        ) {
          child.isActive = true;
          break;
        } else if (child.type === 'collapsible') {
          for (const page of child.pages) {
            if (
              typeof page.to === 'object' &&
              'name' in page.to &&
              currentRoute.matched.filter((r) => !!r.name)[0]?.name === page.to.name
            ) {
              page.isActive = true;
              child.isActive = true;
              break;
            }
          }
        }
      }
    }
  }
}
